import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Sparkles, Upload, ChevronDown, Stethoscope, RefreshCw, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import { vitalMapApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

const SEV = {
  critical: { color: "#DC2626", bg: "#FEE2E2", label: "Urgent" },
  major: { color: "#EA580C", bg: "#FFEDD5", label: "High risk" },
  minor: { color: "#D97706", bg: "#FEF3C7", label: "Mild" },
  normal: { color: "#16A34A", bg: "#DCFCE7", label: "Stable" },
  no_data: { color: "#94A3B8", bg: "#F1F5F9", label: "No data" },
};

function SeverityPill({ severity, text }) {
  const s = SEV[severity] || SEV.no_data;
  return (
    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ color: s.color, backgroundColor: s.bg }}>
      {text || s.label}
    </span>
  );
}

function SystemCard({ sys }) {
  const [open, setOpen] = useState(false);
  const s = SEV[sys.severity] || SEV.no_data;
  const hasData = sys.severity !== "no_data";
  return (
    <Card className="rounded-3xl overflow-hidden" data-testid={`vm-system-${sys.key}`}>
      <button
        className="w-full p-4 flex items-center gap-3 text-left"
        onClick={() => hasData && sys.biomarkers?.length && setOpen((o) => !o)}
        data-testid={`vm-system-toggle-${sys.key}`}
      >
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-2xl shrink-0" style={{ backgroundColor: s.bg }}>
          {sys.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate">{sys.label}</p>
          <p className="text-xs text-muted-foreground truncate">{sys.severityLabel}</p>
        </div>
        <SeverityPill severity={sys.severity} />
        {hasData && sys.biomarkers?.length > 0 && (
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2">
          {sys.biomarkers.map((b, i) => {
            const bs = SEV[b.severity] || SEV.normal;
            return (
              <div key={i} className="rounded-2xl border border-border p-3" data-testid={`vm-biomarker-${sys.key}-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium min-w-0 truncate">{b.name}</p>
                  <span className="text-sm font-semibold shrink-0" style={{ color: bs.color }}>
                    {b.value != null ? `${b.value}${b.unit ? " " + b.unit : ""}` : bs.label}
                  </span>
                </div>
                {b.range && <p className="text-xs text-muted-foreground mt-0.5">Normal: {b.range}</p>}
                {b.explanation && <p className="text-xs text-muted-foreground mt-1">{b.explanation}</p>}
              </div>
            );
          })}
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
            <Stethoscope className="h-3.5 w-3.5" /> Suggested: {sys.suggestedTest} · {sys.specialist}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function VitalMap() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [patient, setPatient] = useState("all");
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const load = useCallback(async (p) => {
    if (!user?._id) return;
    setLoading(true);
    setInsights(null);
    try {
      const res = await vitalMapApi.get(user._id, p);
      setData(res.data?.vitalMap || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    load(patient);
  }, [load, patient]);

  const runInsights = async () => {
    if (!user?._id) return;
    setInsightsLoading(true);
    try {
      const res = await vitalMapApi.insights(user._id, patient);
      if (res.data?.insights) setInsights(res.data.insights);
      else toast.info("Not enough data for AI insights yet.");
    } catch (e) {
      toast.error("Couldn't generate insights. Please try again.");
    } finally {
      setInsightsLoading(false);
    }
  };

  const overall = data ? SEV[data.overallSeverity] || SEV.normal : SEV.normal;
  const filters = data?.patientFilters || [];

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Vital Map"
        subtitle="A whole-body verdict built from your uploaded reports"
        icon={Activity}
        color="#0EA5E9"
      />

      {filters.length > 1 && (
        <div className="flex items-center gap-3">
          <Select value={patient} onValueChange={setPatient}>
            <SelectTrigger className="w-56 rounded-2xl" data-testid="vm-patient-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filters.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`vm-patient-${f.id}`}>
                  {f.name} ({f.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => load(patient)} data-testid="vm-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 rounded-3xl" />
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-3xl" />)}
        </div>
      ) : !data || !data.hasData ? (
        <Card className="rounded-3xl p-10 text-center border-dashed">
          <Activity className="h-12 w-12 text-muted-foreground mx-auto" />
          <p className="mt-4 font-medium">No health data yet</p>
          <p className="text-sm text-muted-foreground mt-1">Upload lab reports to build your organ-system health map.</p>
          <Button className="mt-5 rounded-2xl" onClick={() => navigate("/records")} data-testid="vm-upload-cta">
            <Upload className="h-4 w-4" /> Upload a report
          </Button>
        </Card>
      ) : (
        <>
          {/* Overall summary */}
          <Card className="rounded-3xl p-6" data-testid="vm-summary">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Overall status</p>
                <p className="text-2xl font-semibold mt-1" style={{ color: overall.color }}>{data.overallLabel}</p>
              </div>
              <div className="h-16 w-16 rounded-2xl flex items-center justify-center text-3xl" style={{ backgroundColor: overall.bg }}>
                {data.overallSeverity === "normal" ? "💚" : "⚠️"}
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-medium">Coverage Index</span>
                <span className="text-muted-foreground">{data.systemsWithData}/{data.totalSystems} systems · {data.coverageIndex}%</span>
              </div>
              <Progress value={data.coverageIndex} className="h-2" />
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {["critical", "major", "minor", "normal"].map((k) =>
                data.totals[k] > 0 ? (
                  <SeverityPill key={k} severity={k} text={`${data.totals[k]} ${SEV[k].label}`} />
                ) : null
              )}
            </div>
          </Card>

          {/* AI insights */}
          <Card className="rounded-3xl p-5" data-testid="vm-insights-card">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#F3E8FF" }}>
                <Sparkles className="h-5 w-5" style={{ color: "#7C3AED" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold">Decisive AI verdict</p>
                <p className="text-sm text-muted-foreground">Your top priorities, explained in plain language</p>
              </div>
              {!insights && (
                <Button size="sm" className="rounded-2xl shrink-0" onClick={runInsights} disabled={insightsLoading} data-testid="vm-run-insights">
                  {insightsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {insightsLoading ? "Analyzing" : "Generate"}
                </Button>
              )}
            </div>

            {insights && (
              <div className="mt-4 space-y-3">
                {insights.headline && (
                  <p className="text-sm font-medium bg-accent/50 rounded-2xl p-3">{insights.headline}</p>
                )}
                {(insights.priorities || []).map((p, i) => (
                  <div key={i} className="rounded-2xl border border-border p-4" data-testid={`vm-priority-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm">{p.title}</p>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-primary">{p.actionType}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{p.why}</p>
                    {p.actionLabel && (
                      <Button variant="outline" size="sm" className="rounded-full mt-3" onClick={() => navigate("/find-care")} data-testid={`vm-priority-action-${i}`}>
                        {p.actionLabel}
                      </Button>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 pt-1">
                  <ShieldCheck className="h-3.5 w-3.5" /> AI guidance, not a diagnosis. Consult a doctor for medical decisions.
                </p>
              </div>
            )}
          </Card>

          {/* Systems */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Organ systems</h2>
            {data.systems.map((sys) => <SystemCard key={sys.key} sys={sys} />)}
          </div>

          {/* Missing systems */}
          {data.missingSystems?.length > 0 && (
            <Card className="rounded-3xl p-5" data-testid="vm-missing">
              <p className="font-semibold text-sm">Complete your map</p>
              <p className="text-xs text-muted-foreground mt-0.5 mb-3">No data yet for these systems. Recommended tests:</p>
              <div className="space-y-2">
                {data.missingSystems.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => navigate("/find-labs")}
                    className="w-full flex items-center justify-between gap-2 rounded-2xl border border-border p-3 text-left hover:bg-accent transition-colors"
                    data-testid={`vm-missing-${m.key}`}
                  >
                    <span className="text-sm font-medium">{m.label}</span>
                    <span className="text-xs text-primary font-semibold">{m.suggestedTest} →</span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
